import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import showToast from '../../utils/toastHelper';

const OrganizationElections = () => {
  const { orgId } = useParams();
  const [organization, setOrganization] = useState(null);
  const [elections, setElections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchElections();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const fetchElections = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/elections/organization/${orgId}/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrganization(response.data.organization);
      setElections(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch elections:', error);
      showToast('Failed to load elections', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getElectionStatus = (election) => {
    const now = new Date();
    const startDate = new Date(election.startDate);
    const endDate = new Date(election.endDate);
    
    if (now < startDate) return 'upcoming';
    if (now > endDate) return 'ended';
    return 'active';
  };

  const getStatusBadge = (electionStatus) => {
    const badges = {
      upcoming: { bg: 'warning', icon: 'clock', text: 'Upcoming' },
      active: { bg: 'success', icon: 'check-circle', text: 'Active' },
      ended: { bg: 'secondary', icon: 'x-circle', text: 'Ended' },
    };
    const badge = badges[electionStatus] || badges.ended;
    return (
      <span className={`badge bg-${badge.bg}`} style={{ fontSize: '0.75rem' }}>
        <i className={`bi bi-${badge.icon} me-1`}></i>
        {badge.text}
      </span>
    );
  };

 const getTimeUntilStart = (startDate) => {
  const now = new Date();
  const start = new Date(startDate);

  if (start <= now) return "Started";

  const diffMs = start - now;
  const diffHours = diffMs / (1000 * 60 * 60);

  // Same calendar day
  if (now.toDateString() === start.toDateString()) {
    if (diffHours < 1) {
      return "Starts in less than an hour";
    }
    return `Starts in ${Math.ceil(diffHours)} hour${Math.ceil(diffHours) > 1 ? "s" : ""}`;
  }

  // Tomorrow
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);

  if (tomorrow.toDateString() === start.toDateString()) {
    return "Starts tomorrow";
  }

  const diffDays = Math.round(
    (new Date(start.getFullYear(), start.getMonth(), start.getDate()) -
      new Date(now.getFullYear(), now.getMonth(), now.getDate())) /
      (1000 * 60 * 60 * 24)
  );

  if (diffDays < 7) return `Starts in ${diffDays} days`;

  if (diffDays < 30) {
    const weeks = Math.ceil(diffDays / 7);
    return `Starts in ${weeks} week${weeks > 1 ? "s" : ""}`;
  }

  return `Starts on ${formatDate(startDate).split(",")[0]}`;
};

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="mb-4">
        <Link to="/voter/organizations" style={{ color: '#6b7280', textDecoration: 'none' }}>
          <i className="bi bi-arrow-left me-2"></i>Back to Organizations
        </Link>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 style={{ fontWeight: '600' }}>{organization?.name || 'Organization'}</h3>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
            Active nominations you can participate in
          </p>
        </div>
      </div>

      {elections.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="bi bi-calendar-x" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
            <p className="mt-3 text-muted">No active nominations available for this organization at the moment.</p>
          </div>
        </div>
      ) : (
        <div className="row">
          {elections.map((election) => (
            <div key={election.id} className="col-md-12 mb-4">
              <div className="card">
                <div className="card-body">
                  <div className="mb-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h5 className="mb-0" style={{ fontWeight: '600' }}>{election.name}</h5>
                      {getStatusBadge(getElectionStatus(election))}
                    </div>
                    {election.description && (
                      <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                        {election.description}
                      </p>
                    )}
                    {getElectionStatus(election) === 'upcoming' && (
                      <div className="alert alert-warning mt-2 mb-0 py-2 px-3" style={{ fontSize: '0.813rem' }}>
                        <i className="bi bi-info-circle me-2"></i>
                        {getTimeUntilStart(election.startDate)}
                      </div>
                    )}
                  </div>

                  <div className="row mt-3 pt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
                    <div className="col-6 col-md-3 mb-3">
                      <small className="text-muted text-uppercase d-block" style={{ fontSize: '0.7rem', fontWeight: '600' }}>Start Date</small>
                      <span style={{ fontSize: '0.875rem' }}>{formatDate(election.startDate)}</span>
                    </div>
                    <div className="col-6 col-md-3 mb-3">
                      <small className="text-muted text-uppercase d-block" style={{ fontSize: '0.7rem', fontWeight: '600' }}>End Date</small>
                      <span style={{ fontSize: '0.875rem' }}>{formatDate(election.endDate)}</span>
                    </div>
                    <div className="col-6 col-md-3 mb-3">
                      <small className="text-muted text-uppercase d-block" style={{ fontSize: '0.7rem', fontWeight: '600' }}>Positions</small>
                      <span style={{ fontSize: '0.875rem' }}>{election.positions?.length || 0}</span>
                    </div>
                    <div className="col-6 col-md-3 mb-3">
                      <small className="text-muted text-uppercase d-block" style={{ fontSize: '0.7rem', fontWeight: '600' }}>Candidates</small>
                      <span style={{ fontSize: '0.875rem' }}>
                        {election.positions?.reduce((sum, pos) => sum + (pos.candidates?.length || 0), 0) || 0}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3">
                    {getElectionStatus(election) === 'active' ? (
                      <Link
                        to={`/voter/elections/${election.id}/vote`}
                        className="btn w-100"
                        style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem 1.5rem',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                        }}
                      >
                        <i className="bi bi-box-arrow-in-right me-2"></i>
                        Vote Now
                      </Link>
                    ) : getElectionStatus(election) === 'upcoming' ? (
                      <button
                        className="btn w-100"
                        disabled
                        style={{
                          background: '#e5e7eb',
                          color: '#9ca3af',
                          border: 'none',
                          padding: '0.75rem 1.5rem',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          cursor: 'not-allowed',
                        }}
                      >
                        <i className="bi bi-clock me-2"></i>
                        Not Started
                      </button>
                    ) : (
                      <Link
                        to={`/voter/elections/${election.id}/results`}
                        className="btn btn-outline-secondary w-100"
                        style={{
                          padding: '0.75rem 1.5rem',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                        }}
                      >
                        <i className="bi bi-bar-chart me-2"></i>
                        View Results
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrganizationElections;
